import React, { useContext, useEffect, useState } from 'react'
import styles from './EmailOnly.module.css'
import { FormTitle } from '../../../Common/FormTitle'
import { NextButton } from '../../../Common/CtaButton/NextButton'
import { FormField } from '../../../Common/FormField'
import { validateEmail } from '../LoginInformation/validate'
import { useAbTestGtm } from '../../../../hooks/useAbTestGtm'
import { StepContext, StepIndexContext } from '../../StepContainer'
import { checkAgPrefill } from '../../../../app/api/registrationApi'
import { AgPrefill } from '../../../../app/api/registrationApi/types'
import { StepState as StepStatusState } from '../../../Store/reducers/stepReducer/types'
import { planDiscard, planPrefill } from './agPrefillPlan'

const stepStatusKey = (index: number) => `step${index}` as keyof StepStatusState

// DEV-3482: B/Cパターン専用。Aパターンの LoginInformation（メール+パスワード同居）を
// メールアドレスのみのステップに分割したもの。バリデーションはLoginInformation/validate.tsを再利用する。
// AG会員転記（照合API・AgPrefillService・登録時マージ・agPrefillPlan）は DEV-3255 の実装を
// 引き継いでいるため、それらのコメントは DEV-3255 のままにしてある
export const EmailOnly = () => {
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const {
        formState,
        testPattern,
        handleNext,
        setFormValue,
        setDisplayValue,
        setStepStatus,
        setSkippedSteps,
        skippedSteps,
    } = useContext(StepContext)
    const stepIndex = useContext(StepIndexContext)
    // DEV-3482: ログイン導線は1問目にだけ置く（Bはこの画面が1問目、Cは就業状況が1問目で
    // そちらに同じ導線がある）。Figma 2256:21745 ではCのメール画面に導線は無い
    const showLoginLink = stepIndex === 0
    const [email, setEmail] = useState('')
    const [isFormValid, setIsFormValid] = useState(false)
    const [error, setError] = useState('')
    const [touched, setTouched] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        const validate = async () => {
            const validationError = await validateEmail(email, touched)
            setError(validationError)
            setIsFormValid(!!email && !validationError)
        }

        validate()
    }, [email, touched])

    // AG会員の登録内容をフォームへ流し込み、埋まったステップを飛ばす。
    // 飛ばすには stepStatus（進む側）と skippedSteps（戻る側）の両方が要る
    const applyPrefill = (prefill: AgPrefill) => {
        const {
            values,
            displayValues,
            skippedSteps: skipped,
            reopenSteps,
        } = planPrefill(
            prefill,
            {
                experienceYears: formState.experienceYears,
                tel: formState.tel,
            },
            email
        )

        values.forEach(([key, value]) => setFormValue(key, value))
        // 飛ばしたステップは「次へ」を通らないため、確認画面に出す値はここで入れる
        displayValues.forEach(([key, value]) => setDisplayValue(key, value))
        skipped.forEach(index => setStepStatus(stepStatusKey(index), true))
        reopenSteps.forEach(index => setStepStatus(stepStatusKey(index), false))
        setSkippedSteps(skipped)
    }

    // メールアドレスを変えたら転記も無効にする。氏名ステップは姓名・カナごと未回答に戻す
    const discardPrefill = () => {
        const { clearKeys, clearDisplayKeys, resetSteps } =
            planDiscard(skippedSteps)

        clearKeys.forEach(key => setFormValue(key, ''))
        clearDisplayKeys.forEach(key => setDisplayValue(key, ''))
        resetSteps.forEach(index => setStepStatus(stepStatusKey(index), false))
        setSkippedSteps([])
    }

    const handleSubmit = async () => {
        // 同じメールアドレスで通り直すだけなら照合しない。転記の有無に関わらず飛ばすのは、
        // 戻る・進むを繰り返すたびにレート制限（15回/10分/IP）を消費しないため。
        // 同じメールで結果が変わるのは、この間に本人がAG登録した場合などごく稀。
        // 判定に formState.email ではなく照合済みの印を使うのは、登録に失敗したとき
        // （ContentConfirmation が印を捨てる）に同じメールでも照合し直させるため
        if (email === formState.agPrefillCheckedEmail) {
            handleNext()
            return
        }
        if (formState.agPrefillAppliedEmail) discardPrefill()

        setIsSubmitting(true)
        let prefill: AgPrefill | null = null
        try {
            prefill = (await checkAgPrefill(email)).prefill
        } catch {
            // 照合できなくても登録自体は続けられるので、通常フローへ倒す
        }
        setIsSubmitting(false)

        // 転記が無かった場合も印を付ける。付けないと戻る・進むのたびに照合してしまう
        setFormValue('agPrefillCheckedEmail', email)
        if (prefill) applyPrefill(prefill)
        handleNext()
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <FormTitle
                    formTitleText="メールアドレスを決めましょう"
                    dataViewLabel={dataViewLabel('EF_StepForm_メールアドレス')}
                    dataClickLabel={dataClickLabel(
                        'EF_StepForm_メールアドレス-戻る'
                    )}
                />
                <div className={styles.mailField}>
                    <FormField
                        label="メールアドレス"
                        placeholder="メールアドレスを入力(ログインID)"
                        value={email}
                        // DEV-3482: メール専用ステップにしたので、スマホのキーボードと
                        // ブラウザの自動入力を効かせる。type は A（LoginInformation）と
                        // 同じ text のままにして、ネイティブの検証UIが増えないようにする
                        inputMode="email"
                        autoComplete="email"
                        onBlur={() => setTouched(true)}
                        onChange={setEmail}
                    />
                    {error && <p className={styles.errorMessages}>{error}</p>}
                    {showLoginLink && (
                        <p className={styles.loginText}>
                            <a
                                href={'/login'}
                                data-click-label={dataClickLabel(
                                    'EF_StepForm_メールアドレス-会員登録済み'
                                )}
                            >
                                会員登録済みの方はこちら
                            </a>
                        </p>
                    )}
                </div>
                <NextButton
                    formKey="email"
                    formValue={email}
                    displayFormKey="email"
                    displayFormValue={email}
                    isDisabled={!isFormValid}
                    isLoading={isSubmitting}
                    // AG会員の照合はBパターンだけ。Cパターンは従来どおり素通しする
                    onSubmit={testPattern === 'B' ? handleSubmit : undefined}
                    dataClickLabel={dataClickLabel(
                        'EF_StepForm_メールアドレス-次へ'
                    )}
                />
            </div>
        </div>
    )
}
